import { useState } from "react";
import { UploadCloud, FileMusic, X, Sparkles } from "lucide-react";

export default function UploadCard({ file, onFile, onAnalyze, onReset, fileInputRef }) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-6 sm:p-8 shadow-2xl">
      <div className="mb-6">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[0.95]">
          Find the<br />
          <span className="text-[#FF2D55] led-glow">key</span> of any song
        </h1>
        <p className="text-sm text-zinc-500 mt-3 font-body leading-relaxed">
          Drop an audio file. Get the musical key, Camelot code, and relative key in seconds.
        </p>
      </div>

      {!file ? (
        <label
          htmlFor="file-input"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-10 cursor-pointer transition-all duration-300 group ${
            dragOver
              ? "border-[#FF2D55] bg-[#1A0A10]"
              : "border-white/10 bg-[#0F0F13] hover:border-[#FF2D55]/40 hover:bg-[#14141A]"
          }`}
        >
          <div className="w-14 h-14 rounded-2xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center mb-4 group-hover:border-[#FF2D55]/40 transition-colors">
            <UploadCloud
              className="w-6 h-6 text-zinc-500 group-hover:text-[#FF2D55] transition-colors"
              strokeWidth={1.8}
            />
          </div>
          <div className="text-base font-semibold text-white mb-1.5 font-display">
            Choose audio file
          </div>
          <div className="text-xs text-zinc-500 mb-3">
            or <span className="text-[#FF2D55] underline-offset-2 group-hover:underline">browse</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-mono">
            mp3 · wav · m4a · flac · ogg · max 100mb
          </div>
          <input
            id="file-input"
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.m4a,.ogg,.flac,.aac,.aiff,.aif,.webm,.mp4"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div className="bg-[#0F0F13] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#FF2D55]/10 border border-[#FF2D55]/20 flex items-center justify-center shrink-0">
            <FileMusic className="w-5 h-5 text-[#FF2D55]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-sm font-semibold text-white truncate font-body"
              data-testid="selected-file-name"
              title={file.name}
            >
              {file.name}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
              {(file.size / 1024 / 1024).toFixed(2)} mb · ready to analyze
            </div>
          </div>
          <button
            onClick={onReset}
            data-testid="clear-file-button"
            aria-label="Clear selected file"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <button
        onClick={() => onAnalyze()}
        disabled={!file}
        data-testid="analyze-button"
        className="mt-6 w-full bg-[#FF2D55] text-white hover:bg-[#D11B47] disabled:bg-white/5 disabled:text-zinc-600 disabled:cursor-not-allowed disabled:shadow-none font-bold px-8 py-4 rounded-full text-base font-display tracking-tight transition-all shadow-[0_0_24px_rgba(255,45,85,0.25)] hover:shadow-[0_0_36px_rgba(255,45,85,0.45)] flex items-center justify-center gap-2"
      >
        <Sparkles className="w-4 h-4" strokeWidth={2.4} />
        Analyze key
      </button>
    </div>
  );
}
